import React, { FC, memo, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { skipToken } from '@reduxjs/toolkit/query';
import maxBy from 'lodash/maxBy';
import pick from 'lodash/pick';

import { IAccrualDetailedBalanceDTO, IAccrualSummaryBalanceRequestDTO } from 'src/api/dtos';
import AccrualRepository from 'src/api/repositories/ess/v1/accrual.repository';
import {
  AccrualDetailsSection,
  AccrualFutureBalanceSection,
  AccrualSummaryFiltersDropdown,
  AccrualSummaryFutureAbsencesGrid,
  AccrualSummarySection,
} from 'src/features/accrualSummary';
import { AccrualSummaryFilterEnums, FeaturesEnums } from 'src/shared/enums';
import { ComponentWithFeatures } from 'src/shared/hocs';
import { useFeatures, useNavigateByQueryParams, useQuery } from 'src/shared/hooks';
import { parseQuery } from 'src/shared/utils';

export const AccrualSummaryPage: FC = memo(() => {
  const query = useQuery();
  const navigateByQueryParams = useNavigateByQueryParams();
  const hasAccrualSummaryEnabled = useFeatures([FeaturesEnums.AccrualSummary, FeaturesEnums.AccrualsFunction], true);
  const hasAccrualDetailsEnabled = useFeatures(FeaturesEnums.AccrualBalanceDetails);
  const hasScheduleEmployeeAbsencesEnabled = useFeatures(FeaturesEnums.Schedule_Employee_Absences);

  const [selectedAccrual, setSelectedAccrual] = useState<IAccrualDetailedBalanceDTO | undefined>();

  const accrualSummaryFilter: Pick<IAccrualSummaryBalanceRequestDTO, 'accrualFilter'> = useMemo(() => {
    const q = query ? pick(parseQuery(query.toString()), ['accrualFilter']) : {};
    return { accrualFilter: AccrualSummaryFilterEnums.CURRENT, ...q };
  }, [query]);

  const {
    data: summary,
    isLoading: isSummaryLoading,
    isFetching: isSummaryFetching,
  } = AccrualRepository.endpoints.useGetAccrualSummaryQuery({ ...accrualSummaryFilter });

  const [defaultAccrual] = summary?.accruals ?? [];
  const currentAccrual = selectedAccrual ?? defaultAccrual;

  const {
    currentData: benefitTransactions,
    isLoading: isDetailsLoading,
    isFetching: isDetailsFetching,
  } = AccrualRepository.endpoints.useGetAccrualBenefitTransactionsQuery(
    !currentAccrual || !hasAccrualDetailsEnabled ? skipToken : { benefitId: currentAccrual.benefitId },
  );

  const {
    currentData: futureAbsences,
    isLoading: isFutureAbsencesLoading,
    isFetching: isFutureAbsencesFetching,
  } = AccrualRepository.endpoints.useGetFutureAbsencesQuery(
    !selectedAccrual || !hasScheduleEmployeeAbsencesEnabled
      ? skipToken
      : { benefitEmployeeId: selectedAccrual.benefitEmployeeId },
  );

  const handleAccrualFilterSelect = (accrualFilter: AccrualSummaryFilterEnums) => {
    const params = {
      ...accrualSummaryFilter,
      accrualFilter,
    };

    setSelectedAccrual(undefined);
    navigateByQueryParams(params);
  };

  // latest scheduled absence is needed for the future absence recalc that way the engine will figure out the impact of all future absences (from today -> the latest)
  const latestScheduledAbsence = maxBy(futureAbsences, 'startDate');

  return (
    <div id="page-wrapper">
      <Stack paddingX={2} paddingTop={2} paddingBottom={0}>
        <AccrualSummaryFiltersDropdown
          filterValue={accrualSummaryFilter.accrualFilter}
          onSelect={handleAccrualFilterSelect}
        />
      </Stack>

      <Stack direction="row" position="relative" padding={2} flexGrow={1} width="100%">
        <Paper sx={{ padding: 2, width: '100%', overflow: 'hidden' }}>
          <Grid container spacing={2} flexWrap="wrap" width="100%">
            {hasAccrualSummaryEnabled && (
              <Grid item md={12} lg={3} flexShrink={1}>
                <AccrualSummarySection
                  loading={isSummaryLoading || isSummaryFetching}
                  selectedBenefitId={currentAccrual?.benefitId}
                  accruals={summary?.accruals}
                  onSelect={setSelectedAccrual}
                />
              </Grid>
            )}
            {hasAccrualSummaryEnabled && (
              <Grid item md={12} lg={9} flexShrink={1}>
                <Stack direction="column" spacing={4}>
                  {hasAccrualDetailsEnabled && (
                    <AccrualDetailsSection
                      benefitPackageName={summary?.benefitPackageName}
                      currentAccrual={currentAccrual}
                      benefitTransactions={benefitTransactions}
                      loading={isDetailsLoading || isDetailsFetching}
                    />
                  )}

                  <ComponentWithFeatures features={FeaturesEnums.Schedule_Employee_Absences}>
                    <AccrualSummaryFutureAbsencesGrid
                      rows={futureAbsences ?? []}
                      loading={isFutureAbsencesLoading || isFutureAbsencesFetching}
                    />
                  </ComponentWithFeatures>

                  {!!latestScheduledAbsence && (
                    <ComponentWithFeatures features={FeaturesEnums.AccrualsFunction}>
                      <AccrualFutureBalanceSection scheduledAbsence={latestScheduledAbsence} />
                    </ComponentWithFeatures>
                  )}
                </Stack>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Stack>
    </div>
  );
});
